import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectors as sharedSelector } from "@shared/store";
import { actions, selectors } from "@containers/SensorDataFile/store";
import { SensorDataFileForm } from "@containers/SensorDataFile/components";
import { Button, Loader } from "@shared/components";
import { clearSensorDataFile } from "@containers/SensorDataFile/store/actions";
import { useNavigate } from "react-router";
import { getSensorDataFilePageURL } from "@shared/constants";

import "./index.scss";

interface SensorDataFileFormProps {
  onClose: () => void;
}

const SensorDataFileCreateFormContainer = (props: SensorDataFileFormProps) => {
  const { onClose } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loading = useSelector(sharedSelector.getLoading());
  const uploadedFile = useSelector(selectors.getUploadedFile());
  const uploadingFilePercentage = useSelector(selectors.getFileUploadingPercentage());

  const loadingText = useMemo(() => {
    const uploading = uploadingFilePercentage !== null ? uploadingFilePercentage : undefined;
    return uploading === 100 ? "Analyzing..." : `Uploading...${uploading}%`;
  }, [uploadingFilePercentage]);

  useEffect(() => {
    return () => {
      dispatch(clearSensorDataFile());
    };
  }, [dispatch]);

  const submitForm = useCallback(
    (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      dispatch(
        actions.createSensorDataFile.request({
          data: formData,
          cb: (percentage: number) => {
            dispatch(actions.setFilePercentage(percentage));
          },
        }),
      );
    },
    [dispatch],
  );

  const openSubmitedFile = useCallback(
    (id: number) => {
      navigate(getSensorDataFilePageURL(id));
      onClose();
    },
    [onClose, navigate],
  );

  return (
    <div className="sensor-data-file-container">
      <SensorDataFileForm
        onCloseForm={onClose}
        onSubmitForm={submitForm}
        loading={loading}
        uploadedFile={uploadedFile}
      />
      {uploadedFile ? (
        <div className="uploaded-file">
          <div className="uploaded-file-info">Name: {uploadedFile.name}</div>
          <div className="uploaded-file-info">Rate: {uploadedFile.rate}</div>
          <div className="uploaded-file-info">
            Time Range: {uploadedFile.start_time} - {uploadedFile.end_time}
          </div>
          <div>
            <Button type="submit" className="action-btn" onClick={() => openSubmitedFile(uploadedFile.id)}>
              Open
            </Button>
          </div>
        </div>
      ) : null}
      {loading ? <Loader text={loadingText} /> : null}
    </div>
  );
};

export default SensorDataFileCreateFormContainer;
